// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: staff choose who gets the Monday Madness card. Everyone with Microsoft 365 connected
// is listed (the report is sent from their mailbox); switching someone off sets the same
// preference they can change themselves under Calendar settings.

import {Endpoints} from '@app/features/app/constants/Endpoints';
import {http} from '@app/features/platform/transport/RestTransport';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import {Avatar} from '@app/features/ui/components/Avatar';
import {Switch} from '@app/features/ui/components/form/FormSwitch';
import Users from '@app/features/user/state/Users';
import {Trans, useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import {useEffect, useState} from 'react';

interface MondayPerson {
	id: string;
	name: string;
	username: string;
	enabled: boolean;
}

export const MondayReportPeople = observer(({className}: {className?: string}) => {
	const {t} = useLingui();
	const [people, setPeople] = useState<Array<MondayPerson> | null>(null);
	const [failed, setFailed] = useState(false);
	useEffect(() => {
		void http
			.get<{people: Array<MondayPerson>}>(Endpoints.KONVO_MONDAY_REPORT_PEOPLE)
			.then((response) => setPeople(response.body?.people ?? []))
			.catch(() => setFailed(true));
	}, []);
	const change = (person: MondayPerson, enabled: boolean) => {
		setPeople((list) => list?.map((entry) => (entry.id === person.id ? {...entry, enabled} : entry)) ?? list);
		void http.put(Endpoints.KONVO_MONDAY_REPORT_PERSON(person.id), {body: {enabled}}).catch(() => {
			setPeople((list) => list?.map((entry) => (entry.id === person.id ? person : entry)) ?? list);
			ToastCommands.error(t`Could not change Monday Madness for ${person.name}.`);
		});
	};
	if (failed)
		return (
			<p className={className}>
				<Trans>Could not load the list of people.</Trans>
			</p>
		);
	if (!people) return null;
	if (people.length === 0)
		return (
			<p className={className}>
				<Trans>Nobody has Microsoft 365 connected yet.</Trans>
			</p>
		);
	return (
		<div className={className} data-flx="konvo-monday.people">
			{people.map((person) => {
				const user = Users.getUser(person.id);
				return (
					<Switch
						key={person.id}
						label={person.name}
						description={`@${person.username}`}
						value={person.enabled}
						onChange={(enabled) => change(person, enabled)}
						icon={user ? <Avatar user={user} size={32} /> : undefined}
						data-flx="konvo-monday.people.person"
					/>
				);
			})}
		</div>
	);
});
